import {useContext, useEffect} from "react";

import {GuideStateApi} from "@khan/guide-state";
import {AppShellContext} from "@khan/shared-core/context";

import {GUIDE_ACKNOWLEDGEMENT_PROMO_NAME} from "../constants.ts";
import {usePromotionQuery} from "../queries/use-promotion-query.ts";

/**
 * Update the state indicating if the AI Guide usage terms have been accepted.
 *
 * This hook refreshes via a GraphQL query the state of whether or not a user
 * has accepted the ai terms.
 */
export function useUpdateTermsAcknowledgementEffect(): void {
    const {entrypoint} = useContext(AppShellContext);

    // NOTE(reid): In Classroom (Polaris), we've decided that we don't need to
    // show the guide terms to users. We expect that the district partnership
    // will include accepting the guide terms as part of the partnership.
    // https://khanacademy.slack.com/archives/C07V6SWK47L/p1749235501846489
    const isClassroom = entrypoint === "classroom";

    // Check to see if a user has accepted the guide acknowledgement for
    // AIGuide terms. We skip the query if the state indicates that the user
    // has already accepted the terms - this makes testing easier as by setting
    // that state, the query is skipped entirely. We also skip the query if the
    // entrypoint is classroom, as we don't need to query (see above).
    const hasAcceptedAcknowledgementQueryResult = usePromotionQuery(
        GUIDE_ACKNOWLEDGEMENT_PROMO_NAME,
        GuideStateApi.latest().hasAcknowledgedGuide || isClassroom,
    );

    // Update the current state if the promo query has resolved
    useEffect(() => {
        if (isClassroom || hasAcceptedAcknowledgementQueryResult) {
            GuideStateApi.set("hasAcknowledgedGuide", true);
        }
    }, [hasAcceptedAcknowledgementQueryResult, isClassroom]);
}
